import dayjs from "@/helpers/dayjs";
import { capitalize } from "@/helpers/filters";
import { del, get } from "@/requests/server";
import RestModule from "../RestModule";
import i18n from "@/i18n";

export default new RestModule(
  "loanables",
  {
    empty: {
      type: null,
      comments: "",
      instructions: "",
      return_instructions: "",
      location_description: "",
      name: "",
      details: {},
      position: null,
      images: [],
      availability_json: "[]",
      availability_mode: "never",
      owner_user: {},
    },
    form: {
      general: {
        name: {
          type: "text",
          rules: {
            required: true,
          },
        },
        images: {
          type: "images",
          aspect_ratio: "16 / 10",
        },
        position: {
          type: "point",
        },
        location_description: {
          type: "textarea",
        },
        comments: {
          type: "textarea",
        },
        instructions: {
          type: "textarea",
        },
        return_instructions: {
          type: "textarea",
        },
        type: {
          type: "select",
          options: [
            {
              text: capitalize(i18n.t("loanables.fields.types.car")),
              value: "car",
            },
            {
              text: capitalize(i18n.t("loanables.fields.types.bike")),
              value: "bike",
            },
            {
              text: capitalize(i18n.t("loanables.fields.types.trailer")),
              value: "trailer",
            },
            {
              text: capitalize(i18n.t("loanables.fields.types.car_trailer")),
              value: "car_trailer",
            },
          ],
          rules: {
            required: true,
            oneOf: ["bike", "car", "trailer", "car_trailer"],
          },
        },
        sharing_mode: {
          type: "select",
          options: [
            {
              text: capitalize(i18n.t("loanables.fields.sharing_mode.options.self_service")),
              value: "self_service",
            },
            {
              text: capitalize(i18n.t("loanables.fields.sharing_mode.options.on_demand")),
              value: "on_demand",
            },
            {
              text: capitalize(i18n.t("loanables.fields.sharing_mode.options.hybrid")),
              value: "hybrid",
            },
          ],
          rules: {
            oneOf: ["on_demand", "self_service", "hybrid"],
          },
          pricing_parameters_file: {
            type: "file",
          },
        },
      },
      bike: {
        model: {
          type: "text",
          rules: [],
        },
        bike_type: {
          type: "select",
          options: [
            {
              text: capitalize(i18n.t("bikes.fields.bike_type.options.regular")),
              value: "regular",
            },
            {
              text: capitalize(i18n.t("bikes.fields.bike_type.options.electric")),
              value: "electric",
            },
            {
              text: capitalize(i18n.t("bikes.fields.bike_type.options.cargo")),
              value: "cargo",
            },
            {
              text: capitalize(i18n.t("bikes.fields.bike_type.options.cargo_electric")),
              value: "cargo_electric",
            },
            {
              text: capitalize(i18n.t("bikes.fields.bike_type.options.fixed_wheel")),
              value: "fixed_wheel",
            },
          ],
          rules: {
            oneOf: ["regular", "cargo", "cargo_electric", "electric", "fixed_wheel"],
          },
        },
        size: {
          type: "select",
          options: [
            {
              text: capitalize(i18n.t("bikes.fields.size.options.big")),
              value: "big",
            },
            {
              text: capitalize(i18n.t("bikes.fields.size.options.medium")),
              value: "medium",
            },
            {
              text: capitalize(i18n.t("bikes.fields.size.options.small")),
              value: "small",
            },
            {
              text: capitalize(i18n.t("bikes.fields.size.options.kid")),
              value: "kid",
            },
          ],
          rules: {
            oneOf: ["big", "medium", "small", "kid"],
          },
        },
      },
      car: {
        brand: {
          type: "text",
          rules: [],
        },
        model: {
          type: "text",
          rules: [],
        },
        pricing_category: {
          type: "select",
          options: [
            {
              text: capitalize(i18n.t("cars.fields.pricing_category.options.car_small")),
              value: "small",
            },
            {
              text: capitalize(i18n.t("cars.fields.pricing_category.options.car_large")),
              value: "large",
            },
            {
              text: capitalize(i18n.t("cars.fields.pricing_category.options.car_small_electric")),
              value: "small_electric",
            },
            {
              text: capitalize(i18n.t("cars.fields.pricing_category.options.car_large_electric")),
              value: "large_electric",
            },
          ],
          rules: {
            oneOf: ["small", "large", "small_electric", "large_electric"],
          },
        },
        year_of_circulation: {
          type: "number",
          max: dayjs().add(1, "year").format("YYYY"),
          min: 1900,
          rules: {
            numeric: true,
            nullable: true,
            digits: 4,
            max_value: dayjs().add(1, "year").format("YYYY"),
          },
        },
        transmission_mode: {
          type: "select",
          options: [
            {
              text: capitalize(i18n.t("cars.fields.transmission_mode.options.automatic")),
              value: "automatic",
            },
            {
              text: capitalize(i18n.t("cars.fields.transmission_mode.options.manual")),
              value: "manual",
            },
          ],
          rules: {
            oneOf: ["manual", "automatic"],
          },
        },
        engine: {
          type: "select",
          options: [
            {
              text: capitalize(i18n.t("cars.fields.engine.options.fuel")),
              value: "fuel",
            },
            {
              text: capitalize(i18n.t("cars.fields.engine.options.diesel")),
              value: "diesel",
            },
            {
              text: capitalize(i18n.t("cars.fields.engine.options.electric")),
              value: "electric",
            },
            {
              text: capitalize(i18n.t("cars.fields.engine.options.hybrid")),
              value: "hybrid",
            },
          ],
          rules: {
            oneOf: ["fuel", "diesel", "electric", "hybrid"],
          },
        },
        plate_number: {
          type: "text",
          rules: [],
        },
        value_category: {
          type: "select",
          options: [
            {
              text: capitalize(i18n.t("cars.fields.value_category.options.lte50k")),
              value: "lte50k",
            },
            {
              text: capitalize(i18n.t("cars.fields.value_category.options.lte70k")),
              value: "lte70k",
            },
            {
              text: capitalize(i18n.t("cars.fields.value_category.options.lte100k")),
              value: "lte100k",
            },
          ],
          rules: {
            oneOf: ["lte50k", "lte70k", "lte100k"],
          },
        },
        has_onboard_notebook: {
          type: "checkbox",
        },
        has_report_in_notebook: {
          type: "checkbox",
        },
        report: {
          type: "file",
        },
        papers_location: {
          type: "select",
          options: [
            {
              text: capitalize(i18n.t("cars.fields.papers_location.options.in_the_car")),
              value: "in_the_car",
            },
            {
              text: capitalize(i18n.t("cars.fields.papers_location.options.to_request_with_car")),
              value: "to_request_with_car",
            },
          ],
          rules: {
            oneOf: ["in_the_car", "to_request_with_car"],
          },
        },
        insurer: {
          type: "text",
          rules: [],
        },
        has_informed_insurer: {
          type: "checkbox",
        },
      },
      trailer: {
        maximum_charge: {
          type: "text",
        },
        dimensions: {
          type: "text",
        },
      },
    },
  },
  {
    /**
     * Attempts to retrieve a loanable which may be accessible by the user
     */
    async tryRetrieveOne({ commit, state }, { params, id }) {
      commit("loaded", false);
      commit("loading", true);

      try {
        const { data } = await get(`/${state.slug}/${id}`, {
          axiosRequestConfig: { params },
          notifications: { action: i18n.t("store.models.loanables.notifications.retrieve.action") },
          cleanupCallback: () => commit("loading", false),
          requestOptions: { expects: [404] },
        });
        commit("item", data);
        commit("initialItem", data);

        commit("loaded", true);
        commit("loading", false);
      } catch (e) {
        if (e.request && e.request.status === 404) {
          commit(
            "addNotification",
            {
              content: i18n.t("store.models.loanables.errors.inaccessible.content"),
              title: i18n.t("store.models.loanables.errors.inaccessible.title"),
              variant: "danger",
            },
            { root: true }
          );
        }
        throw e;
      }
    },
    async disable({ commit, state }, id) {
      const { data: deleted } = await del(`/${state.slug}/${id}`, {
        notifications: {
          action: i18n.t("store.models.loanables.notifications.disable.action"),
          onSuccess: i18n.t("store.models.loanables.notifications.disable.success"),
        },
      });

      commit("deleted", deleted);
    },
  }
);
